import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import '@aws-amplify/ui-react/styles.css';

// Import components
import Navigation from './components/Navigation';
import ImproveResume from './components/ImproveResume';
import Contact from './components/Contact';
import Results from './components/Results';
import Landing from './components/Landing';
import Home from './components/Home';
import MockInterview from './components/MockInterview';
import Jobs from './components/Jobs';
import Pricing from './components/Pricing';
import UserProfile from './components/UserProfile';
import PortfolioBuilder from './components/PortfolioBuilder';
import SubscriptionStatus from './components/SubscriptionStatus'; 
import ProgressDashboard from './components/ProgressDashboard';
import ImprovementRoadmap from './components/ImprovementRoadmap';
import InterviewResults from './components/InterviewResults';

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchAuthSession();
        const hasTokens = session?.tokens?.accessToken?.toString() !== undefined;
        setIsAuthenticated(hasTokens);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, []);

  const handleGetStarted = async () => {
    try {
      setIsLoading(true);
      await signInWithRedirect({
        provider: 'Cognito'
      });
    } catch (error) {
      console.error('Failed to start auth:', error);
      setIsLoading(false);
    }
  };

  // Show loading while checking auth
  if (isLoading || !authChecked) {
    return <div>Loading...</div>;
  }

  console.log('Rendering router with auth state:', { 
    isAuthenticated, 
    isLoading, 
    authChecked,
    currentPath: window.location.pathname 
  });

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {isAuthenticated && <Navigation />}
        <Routes>
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                <Navigate to="/home" replace />
              ) : (
                <Landing onGetStarted={handleGetStarted} />
              )
            } 
          />
          <Route 
            path="/home" 
            element={
              isAuthenticated ? (
                <Home />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/improve-resume" 
            element={
              isAuthenticated ? (
                <ImproveResume />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/contact" 
            element={
              isAuthenticated ? (
                <Contact />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/results" 
            element={
              isAuthenticated ? (
                <Results />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/MockInterview" 
            element={
              isAuthenticated ? (
               <MockInterview />
                     ) : (
                  <Navigate to="/" replace />
                   )
                } 
              />
          <Route 
            path="/Jobs" 
            element={
              isAuthenticated ? (
               <Jobs />
                     ) : (
                  <Navigate to="/" replace />
                   )
                } 
              />
          <Route 
            path="/pricing" 
            element={<Pricing />}  // Removed authentication check
          />
          <Route 
            path="/profile" 
            element={
              isAuthenticated ? (
                <UserProfile />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/auto-apply" 
            element={
              isAuthenticated ? (
                <div className="flex items-center justify-center min-h-screen">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Auto Apply</h1>
                    <p className="text-gray-600">Coming soon! This feature will allow you to automate your job application process.</p>
                  </div>
                </div>
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/referrals" 
            element={
              isAuthenticated ? (
                <div className="flex items-center justify-center min-h-screen">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Referrals</h1>
                    <p className="text-gray-600">Coming soon! This feature will help you connect with professionals and get referral opportunities.</p>
                  </div>
                </div>
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/portfolio-builder" 
            element={
              isAuthenticated ? (
                <PortfolioBuilder />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/subscription" 
            element={
              isAuthenticated ? (
                <SubscriptionStatus />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route path="/progress" element={<ProgressDashboard />} />
          <Route path="/roadmap" element={<ImprovementRoadmap />} />
          <Route 
            path="/interview-results" 
            element={
              isAuthenticated ? (
                <InterviewResults />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
        </Routes>

      </div>
    </Router>
  );
};

export default App; 